import React, { useState } from "react";

/* Note Review audit chip (bsfix-name-vocab / hubfix-practice-vocab, 2026-10-19).
   BlinkScribe checks close misspellings of known names (providers, referral
   physicians, the practice's custom terms) in the transcript and the note.
   The daily-review row carries them as name_corrections:
   [{from, to, count, rule, where: [...], applied}].
     applied === false  SUGGEST mode (the default while the precision check is
                        pending): nothing was changed - review only.
     applied true/absent the spelling was changed (APPLY mode); every raw
                        transcript version is kept on the server.
   Audit only: there is no one-click revert - edit the note if a change is
   wrong. name_corrections never appears inside note_json on any client read,
   so it can never be copied or exported. */

const WHERE_LABELS = {
  transcript: "transcript",
  chief_complaint: "chief complaint",
  hpi: "HPI",
  review_of_systems: "ROS",
  physical_examination: "exam",
  assessment_plan: "assessment & plan",
  doctor_instructions: "instructions",
  current_medications: "medications",
};

const RULE_LABELS = {
  title: "after Dr./Doctor",
  pair: "full name",
  alias: "practice list spelling",
};

export const correctionCount = (items) =>
  (Array.isArray(items) ? items : []).reduce((n, c) => n + (Number(c?.count) || 1), 0);

export const isApplied = (c) => c?.applied !== false;

const whereText = (where) =>
  (Array.isArray(where) ? where : where ? [where] : [])
    .map((w) => WHERE_LABELS[w] || String(w).replace(/_/g, " "))
    .join(", ");

export const chipLabel = (items) => {
  const applied = correctionCount(items.filter(isApplied));
  const suggested = correctionCount(items.filter((c) => !isApplied(c)));
  if (applied && suggested) return `Names corrected: ${applied} · suggested: ${suggested}`;
  if (suggested) return `Name suggestions: ${suggested}`;
  return `Names corrected: ${applied}`;
};

export default function NameCorrectionsChip({ corrections }) {
  const [open, setOpen] = useState(false);
  const items = (Array.isArray(corrections) ? corrections : []).filter((c) => c && c.from && c.to);
  if (!items.length) return null;
  const anyApplied = items.some(isApplied);
  const anySuggested = items.some((c) => !isApplied(c));
  return (
    <div className="px-5 py-2 border-b" data-testid="name-corrections">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={anyApplied
          ? "BlinkScribe corrected these name spellings automatically. Click to see what changed."
          : "BlinkScribe would correct these name spellings. Nothing was changed. Click to see them."}
        className={anyApplied
          ? "px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#EFF6FF] text-[#1D4ED8] border border-[#93C5FD]"
          : "px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F9FAFB] text-[#4B5563] border border-[#D1D5DB]"}
      >
        {chipLabel(items)}
      </button>
      {open && (
        <div className="mt-2 text-xs text-gray-700">
          <div className="text-gray-500 mb-1">
            {anyApplied && "Spelling fixed automatically to match the practice's known names. Edit the note if a change is wrong."}
            {anyApplied && anySuggested && " "}
            {anySuggested && "Suggestions only: the note still has the spelling as heard."}
          </div>
          <ul className="flex flex-col gap-0.5">
            {items.map((c) => (
              <li key={`${c.from}->${c.to}:${isApplied(c) ? "a" : "s"}`}>
                <span className={isApplied(c) ? "line-through text-gray-500" : "text-gray-700"}>{c.from}</span>
                {" → "}
                <span className="font-semibold">{c.to}</span>
                {(Number(c.count) || 1) > 1 ? ` ×${c.count}` : ""}
                {!isApplied(c) ? <span className="text-gray-500"> · suggested, not applied</span> : null}
                {whereText(c.where) ? <span className="text-gray-500"> · {whereText(c.where)}</span> : null}
                {RULE_LABELS[c.rule] ? <span className="text-gray-400"> · {RULE_LABELS[c.rule]}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
